import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Card from '@components/ui/Card';
import Badge from '@components/ui/Badge';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import ErrorState from '@components/ui/ErrorState';
import SigningRequests, { SigningRequestSummary } from '@components/ui/SigningRequests';
import './Projects.css';
import './PeopleDetail.css';
import './PeopleForm.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LinkedPerson {
  id: string;
  full_name: string;
  email: string | null;
  status: string;
  role_or_title: string | null;
}

interface ProjectDocument {
  id: string;
  document_name: string;
  document_type: string;
  uploaded_at: string;
  created_at: string;
}

interface ChatIntegration {
  id: string;
  project_id: string;
  github_owner: string | null;
  github_repo: string | null;
  github_installation_id: string | null;
  google_chat_space_id: string | null;
  google_chat_space_display_name: string | null;
  status: 'pending_github' | 'pending_chat' | 'connected' | 'warning_all_repos' | 'error';
}

interface GitHubAppInstallationRepo {
  installation_id: number;
  owner: string;
  repo: string;
  full_name: string;
}

interface PersonSuggestion {
  id: string;
  full_name: string;
  company_name: string | null;
}

interface ProjectDetail {
  id: string;
  project_name: string;
  status: string;
  project_type: string | null;
  cost_model: string | null;
  general_notes: string | null;
  company_id: string | null;
  company_name: string | null;
  lead_person_id: string | null;
  lead_person_name: string | null;
  lead_person_email: string | null;
  estimated_start_date: string | null;
  estimated_close_date: string | null;
  started_date: string | null;
  closed_date: string | null;
  github_repo_url: string | null;
  github_repo_full_name: string | null;
  github_repo_name: string | null;
  github_repo_id: string | null;
  github_repo_status: string;
  github_repo_error: string | null;
  github_links: string | null;
  days_worked: number | null;
  cost_summary: number | null;
  billed_amount: number | null;
  profit: number | null;
  created_at: string;
  updated_at: string;
  linked_people: LinkedPerson[];
  documents?: ProjectDocument[];
  signing_requests?: SigningRequestSummary[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function projectStatusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Work Started':
    case 'Work Finished':
      return 'warning';
    default:
      return 'default';
  }
}

function personStatusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'Customer':
      return 'success';
    case 'Opportunity':
    case 'Qualified':
      return 'warning';
    case 'Unqualified':
      return 'danger';
    default:
      return 'default';
  }
}

function repoStatusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'ready':
      return 'success';
    case 'provisioning':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'default';
  }
}

function chatIntegrationStatusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'connected':
      return 'success';
    case 'pending_github':
    case 'pending_chat':
      return 'default';
    case 'warning_all_repos':
      return 'warning';
    case 'error':
      return 'danger';
    default:
      return 'default';
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// ─── Edit form state ──────────────────────────────────────────────────────────

interface EditFormState {
  project_name: string;
  status: string;
  project_type: string;
  cost_model: string;
  general_notes: string;
  estimated_start_date: string;
  estimated_close_date: string;
  started_date: string;
  closed_date: string;
  billed_amount: string;
  days_worked: string;
}

const PROJECT_STATUSES = [
  'New',
  'Proposal Accepted',
  'Work Started',
  'Work Finished',
  'Completed',
] as const;

// ─── Component ────────────────────────────────────────────────────────────────

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [repoRequesting, setRepoRequesting] = useState(false);
  const [repoRequestError, setRepoRequestError] = useState<string | null>(null);

  // GitHub Repo Connections state (a project may have many)
  const [chatIntegrations, setChatIntegrations] = useState<ChatIntegration[]>([]);
  const [chatIntegrationsLoading, setChatIntegrationsLoading] = useState(true);
  const [addRepoOpen, setAddRepoOpen] = useState(false);
  const [installations, setInstallations] = useState<GitHubAppInstallationRepo[]>([]);
  const [installationsLoading, setInstallationsLoading] = useState(false);
  const [selectedRepoFullName, setSelectedRepoFullName] = useState('');
  const [addingRepo, setAddingRepo] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { github_ok: boolean; reason: string | null }>>({});
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [personSearchQuery, setPersonSearchQuery] = useState('');
  const [personSuggestions, setPersonSuggestions] = useState<PersonSuggestion[]>([]);
  const [personSuggestionsLoading, setPersonSuggestionsLoading] = useState(false);
  const [selectedPersonToAdd, setSelectedPersonToAdd] = useState<PersonSuggestion | null>(null);
  const [addingPerson, setAddingPerson] = useState(false);
  const [addPersonError, setAddPersonError] = useState<string | null>(null);
  const [removingPersonId, setRemovingPersonId] = useState<string | null>(null);

  // Document upload state
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadDocError, setUploadDocError] = useState<string | null>(null);
  const [uploadDocFile, setUploadDocFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState<string>('Meeting notes');
  const [uploadDocOpen, setUploadDocOpen] = useState(false);

  const [form, setForm] = useState<EditFormState>({
    project_name: '',
    status: 'New',
    project_type: '',
    cost_model: '',
    general_notes: '',
    estimated_start_date: '',
    estimated_close_date: '',
    started_date: '',
    closed_date: '',
    billed_amount: '',
    days_worked: '',
  });

  const fetchProject = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/projects/${id}`);
      if (res.status === 404) {
        setError('Project not found.');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as { success: boolean; data: ProjectDetail };
      if (!json.success) throw new Error('API returned failure');
      setProject(json.data);
    } catch (err) {
      console.error('Failed to fetch project:', err);
      setError('Failed to load project. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const fetchChatIntegrations = useCallback(async () => {
    if (!id) return;
    setChatIntegrationsLoading(true);
    try {
      const res = await fetch(`/api/projects/${id}/chat-integration`);
      const json = await res.json() as { success: boolean; data: ChatIntegration[] };
      if (json.success) setChatIntegrations(json.data);
    } catch (err) {
      console.error('Failed to fetch repo connections:', err);
    } finally {
      setChatIntegrationsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchChatIntegrations();
  }, [fetchChatIntegrations]);

  const openAddRepoPanel = async () => {
    setConnectError(null);
    setSelectedRepoFullName('');
    setAddRepoOpen(true);
    setInstallationsLoading(true);
    try {
      const res = await fetch('/api/github-app/installations');
      const json = await res.json() as { success: boolean; data: GitHubAppInstallationRepo[]; message?: string };
      if (!json.success) throw new Error(json.message ?? 'Failed to load repos');
      setInstallations(json.data);
    } catch (err: unknown) {
      setConnectError(err instanceof Error ? err.message : 'Failed to load repos.');
    } finally {
      setInstallationsLoading(false);
    }
  };

  const handleAddRepo = async () => {
    if (!id || !selectedRepoFullName) return;
    const match = installations.find((r) => r.full_name === selectedRepoFullName);
    if (!match) return;

    setAddingRepo(true);
    setConnectError(null);
    try {
      const res = await fetch(`/api/projects/${id}/chat-integration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ github_owner: match.owner, github_repo: match.repo }),
      });
      const json = await res.json() as { success: boolean; message?: string };
      if (!res.ok || !json.success) throw new Error(json.message ?? 'Failed to add repo');
      setAddRepoOpen(false);
      await fetchChatIntegrations();
    } catch (err: unknown) {
      setConnectError(err instanceof Error ? err.message : 'Failed to add repo.');
    } finally {
      setAddingRepo(false);
    }
  };

  const handleTestConnection = async (integrationId: string) => {
    if (!id) return;
    setTestingId(integrationId);
    try {
      const res = await fetch(`/api/projects/${id}/chat-integration/${integrationId}/test`, { method: 'POST' });
      const json = await res.json() as {
        success: boolean;
        data: { github_ok: boolean; status: ChatIntegration['status']; reason: string | null };
      };
      if (json.success) {
        setTestResults((prev) => ({ ...prev, [integrationId]: { github_ok: json.data.github_ok, reason: json.data.reason } }));
        await fetchChatIntegrations();
      }
    } catch (err) {
      console.error('Failed to test repo connection:', err);
    } finally {
      setTestingId(null);
    }
  };

  const handleDisconnectRepo = async (integrationId: string, label: string) => {
    if (!id) return;
    const ok = window.confirm(
      `Disconnect ${label}?\n\nThis only removes the CRM link -- it does not uninstall the GitHub App.`
    );
    if (!ok) return;
    setDisconnectingId(integrationId);
    try {
      const res = await fetch(`/api/projects/${id}/chat-integration/${integrationId}`, { method: 'DELETE' });
      const json = await res.json() as { success: boolean };
      if (json.success) await fetchChatIntegrations();
    } catch (err) {
      console.error('Failed to disconnect repo:', err);
    } finally {
      setDisconnectingId(null);
    }
  };

  const openAddPersonPanel = () => {
    setAddPersonError(null);
    setPersonSearchQuery('');
    setPersonSuggestions([]);
    setSelectedPersonToAdd(null);
    setAddPersonOpen(true);
  };

  useEffect(() => {
    if (!addPersonOpen) return;
    const query = personSearchQuery.trim();
    if (query.length < 2) {
      setPersonSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setPersonSuggestionsLoading(true);
      try {
        const res = await fetch(`/api/people?q=${encodeURIComponent(query)}&page_size=8`);
        if (!res.ok) return;
        const json = await res.json() as { success: boolean; data: PersonSuggestion[] };
        if (json.success) {
          const linkedIds = new Set(project?.linked_people.map((p) => p.id) ?? []);
          setPersonSuggestions(json.data.filter((p) => !linkedIds.has(p.id)));
        }
      } catch {
        // Silently fail -- suggestion list just stays empty
      } finally {
        setPersonSuggestionsLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [personSearchQuery, addPersonOpen, project?.linked_people]);

  const handleAddPerson = async () => {
    if (!id || !project || !selectedPersonToAdd) return;
    setAddingPerson(true);
    setAddPersonError(null);
    try {
      const linkedIds = project.linked_people.map((p) => p.id);
      const res = await fetch(`/api/projects/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ linked_person_ids: [...linkedIds, selectedPersonToAdd.id] }),
      });
      const json = await res.json() as { success: boolean; message?: string };
      if (!res.ok || !json.success) throw new Error(json.message ?? 'Failed to connect person');
      setAddPersonOpen(false);
      await fetchProject();
    } catch (err: unknown) {
      setAddPersonError(err instanceof Error ? err.message : 'Failed to connect person.');
    } finally {
      setAddingPerson(false);
    }
  };

  const handleRemovePerson = async (personId: string, name: string) => {
    if (!id || !project) return;
    const ok = window.confirm(`Remove ${name} from this project's people?`);
    if (!ok) return;
    setRemovingPersonId(personId);
    try {
      const remainingIds = project.linked_people.filter((p) => p.id !== personId).map((p) => p.id);
      const res = await fetch(`/api/projects/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ linked_person_ids: remainingIds }),
      });
      const json = await res.json() as { success: boolean };
      if (json.success) await fetchProject();
    } catch (err) {
      console.error('Failed to remove person:', err);
    } finally {
      setRemovingPersonId(null);
    }
  };

  const startEditing = () => {
    if (!project) return;
    setForm({
      project_name: project.project_name ?? '',
      status: project.status ?? 'New',
      project_type: project.project_type ?? '',
      cost_model: project.cost_model ?? '',
      general_notes: project.general_notes ?? '',
      estimated_start_date: project.estimated_start_date ?? '',
      estimated_close_date: project.estimated_close_date ?? '',
      started_date: project.started_date ?? '',
      closed_date: project.closed_date ?? '',
      billed_amount: project.billed_amount !== null ? String(project.billed_amount) : '',
      days_worked: project.days_worked !== null ? String(project.days_worked) : '',
    });
    setSaveError(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!id || !project) return;
    if (!form.project_name.trim()) {
      setSaveError('Project name is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(`/api/projects/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_name: form.project_name.trim(),
          status: form.status,
          project_type: form.project_type.trim() || null,
          cost_model: form.cost_model.trim() || null,
          general_notes: form.general_notes.trim() || null,
          estimated_start_date: form.estimated_start_date.trim() || null,
          estimated_close_date: form.estimated_close_date.trim() || null,
          started_date: form.started_date.trim() || null,
          closed_date: form.closed_date.trim() || null,
          billed_amount: form.billed_amount.trim() ? parseFloat(form.billed_amount) : null,
          days_worked: form.days_worked.trim() ? parseFloat(form.days_worked) : null,
        }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error((json as { message?: string }).message ?? `HTTP ${res.status}`);
      }
      const json = await res.json() as { success: boolean; data: ProjectDetail };
      if (!json.success) throw new Error('Save failed');
      setProject(json.data);
      setEditing(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save changes.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!id || !project) return;

    const ok = window.confirm(
      `Are you sure you want to delete this project?\n\n${project.project_name}\n\nThis action cannot be undone.`
    );
    if (!ok) return;

    setDeleteSaving(true);
    setDeleteError(null);
    try {
      const res = await fetch(`/api/projects/${id}`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !(json as any)?.success) {
        throw new Error((json as any)?.message ?? `HTTP ${res.status}`);
      }
      navigate('/projects');
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete project.');
    } finally {
      setDeleteSaving(false);
    }
  };

  const resetUploadDocState = () => {
    setUploadDocFile(null);
    setUploadDocType('Meeting notes');
    setUploadDocError(null);
    setUploadDocOpen(false);
  };

  const handleUploadDocument = async () => {
    if (!id || !project) return;
    if (!uploadDocFile) {
      setUploadDocError('Please select a file to upload.');
      return;
    }

    setUploadingDoc(true);
    setUploadDocError(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadDocFile);
      formData.append('document_name', uploadDocFile.name);
      formData.append('document_type', uploadDocType);
      formData.append('project_id', id);

      const res = await fetch('/api/documents', {
        method: 'POST',
        body: formData,
      });

      const json = await res.json().catch(() => ({} as any));
      if (!res.ok || (json as any)?.success !== true) {
        throw new Error((json as any)?.message ?? `Upload failed (HTTP ${res.status})`);
      }

      // Refresh project detail to show new document
      await fetchProject();
      resetUploadDocState();
    } catch (err: unknown) {
      setUploadDocError(err instanceof Error ? err.message : 'Failed to upload document.');
    } finally {
      setUploadingDoc(false);
    }
  };

  const openUploadDocDialog = () => {
    setUploadDocError(null);
    setUploadDocFile(null);
    setUploadDocType('Meeting notes');
    setUploadDocOpen(true);
  };

  const handleDeleteDocument = async (docId: string, docName: string) => {
    if (!id || !project) return;

    const ok = window.confirm(
      `Are you sure you want to delete this document?\n\n${docName}\n\nThis action cannot be undone.`
    );
    if (!ok) return;

    try {
      const res = await fetch(`/api/documents/${docId}`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({} as any));
      if (!res.ok || (json as any)?.success !== true) {
        throw new Error((json as any)?.message ?? `Delete failed (HTTP ${res.status})`);
      }

      // Refresh project detail to remove deleted document
      await fetchProject();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete document.');
    }
  };

  const handleRequestRepoProvisioning = async () => {
    if (!id || !project) return;

    // UI safety
    if (project.github_repo_status === 'provisioning') return;
    if (project.github_repo_status === 'ready' && project.github_repo_url) return;

    setRepoRequesting(true);
    setRepoRequestError(null);
    try {
      const res = await fetch(`/api/projects/${id}/request-github-repo`, {
        method: 'POST',
      });

      const json = await res.json().catch(() => ({} as any));

      if (!res.ok) {
        throw new Error((json as { message?: string }).message ?? `HTTP ${res.status}`);
      }

      if ((json as any)?.success !== true) {
        throw new Error('Request failed');
      }

      // Refresh the project detail so status updates show.
      await fetchProject();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to request repo provisioning.';
      setRepoRequestError(message);
    } finally {
      setRepoRequesting(false);
    }
  };

  // ─── Loading / error states ──────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="detail-loading">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <Card>
        <ErrorState
          message={error ?? 'Project not found.'}
          onRetry={error !== 'Project not found.' ? fetchProject : undefined}
        />
      </Card>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="project-detail-page">
      {/* Back navigation */}
      <button className="back-link" onClick={() => navigate('/projects')} type="button">
        ← Projects
      </button>

      {/* Page header */}
      <div className="detail-header">
        <div className="detail-header-left">
          <h1 className="detail-name">{project.project_name}</h1>
          {project.company_name && (
            <p className="detail-role">{project.company_name}</p>
          )}
        </div>
        <div className="detail-header-right">
          <Badge variant={projectStatusVariant(project.status)}>{project.status}</Badge>
          {!editing && (
            <>
              <Button variant="secondary" size="sm" onClick={startEditing}>
                Edit
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={openUploadDocDialog}
                disabled={uploadingDoc}
              >
                Upload document
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeleteProject}
                disabled={deleteSaving}
              >
                {deleteSaving ? 'Deleting…' : 'Delete'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Upload document dialog */}
      {!editing && uploadDocOpen && (
        <Card>
          <h2 className="section-title">Upload document</h2>
          <div className="edit-form">
            <div className="input-wrapper">
              <label className="input-label">Document type</label>
              <select
                className="input"
                value={uploadDocType}
                onChange={(e) => setUploadDocType(e.target.value)}
                disabled={uploadingDoc}
              >
                <option value="Meeting notes">Meeting notes</option>
                <option value="Signed document">Signed document</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="input-wrapper">
              <label className="input-label">File</label>
              <input
                type="file"
                className="input"
                onChange={(e) => {
                  setUploadDocFile(e.target.files?.[0] || null);
                  setUploadDocError(null);
                }}
                disabled={uploadingDoc}
              />
            </div>

            {uploadDocError && <p className="edit-save-error">{uploadDocError}</p>}

            <div className="edit-actions">
              <Button
                variant="primary"
                onClick={handleUploadDocument}
                disabled={uploadingDoc || !uploadDocFile}
              >
                {uploadingDoc ? 'Uploading…' : 'Upload'}
              </Button>
              <Button variant="secondary" onClick={resetUploadDocState} disabled={uploadingDoc}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {deleteError && (
        <Card>
          <p className="edit-save-error">{deleteError}</p>
        </Card>
      )}

      {/* ── View mode ─────────────────────────────────────────────────────────── */}
      {!editing ? (
        <>
          {/* Company and lead person */}
          <Card>
            <h2 className="section-title">Project details</h2>
            <div className="detail-fields">
              <div className="detail-field">
                <span className="detail-field-label">Company</span>
                {project.company_id ? (
                  <Link to={`/companies/${project.company_id}`} className="detail-company-link">
                    {project.company_name ?? project.company_id}
                  </Link>
                ) : (
                  <span className="detail-field-empty">—</span>
                )}
              </div>
              <div className="detail-field">
                <span className="detail-field-label">Lead</span>
                {project.lead_person_id ? (
                  <Link to={`/people/${project.lead_person_id}`} className="detail-company-link">
                    {project.lead_person_name ?? project.lead_person_id}
                  </Link>
                ) : (
                  <span className="detail-field-empty">—</span>
                )}
              </div>
              {project.project_type && (
                <div className="detail-field">
                  <span className="detail-field-label">Type</span>
                  <span className="detail-field-value">{project.project_type}</span>
                </div>
              )}
              {project.cost_model && (
                <div className="detail-field">
                  <span className="detail-field-label">Cost model</span>
                  <span className="detail-field-value">{project.cost_model}</span>
                </div>
              )}
            </div>
          </Card>

          {/* General notes */}
          <Card>
            <h2 className="section-title">Notes</h2>
            {project.general_notes ? (
              <p className="detail-notes">{project.general_notes}</p>
            ) : (
              <p className="detail-field-empty">No notes yet.</p>
            )}
          </Card>

          {/* Documents */}
          <Card>
            <h2 className="section-title">Documents</h2>
            {(project.documents ?? []).length === 0 ? (
              <p className="detail-field-empty">No documents uploaded.</p>
            ) : (
              <div className="detail-projects-list">
                {(project.documents ?? []).map((d) => (
                  <div
                    key={d.id}
                    className="detail-project-row"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <a
                      href={`/api/documents/${d.id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        cursor: 'pointer',
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-sm)',
                      }}
                    >
                      <span className="detail-project-name">{d.document_name}</span>
                      <Badge variant="default">{d.document_type}</Badge>
                    </a>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteDocument(d.id, d.document_name)}
                    >
                      Delete
                    </Button>
                  </div>
                ))}
              </div>
            )}

          </Card>

          {/* Signing requests */}
          <SigningRequests
            projectId={project.id}
            requests={project.signing_requests ?? []}
            onChanged={fetchProject}
          />

          {/* Dates */}
          <Card>
            <h2 className="section-title">Dates</h2>
            <div className="detail-fields">
              <div className="detail-field">
                <span className="detail-field-label">Est. start</span>
                <span className="detail-field-value">{formatDate(project.estimated_start_date)}</span>
              </div>
              <div className="detail-field">
                <span className="detail-field-label">Started</span>
                <span className="detail-field-value">{formatDate(project.started_date)}</span>
              </div>
              <div className="detail-field">
                <span className="detail-field-label">Est. close</span>
                <span className="detail-field-value">{formatDate(project.estimated_close_date)}</span>
              </div>
              <div className="detail-field">
                <span className="detail-field-label">Closed</span>
                <span className="detail-field-value">{formatDate(project.closed_date)}</span>
              </div>
            </div>
          </Card>

          {/* Linked people */}
          <Card>
            <h2 className="section-title">
              People
              {project.linked_people.length > 0 && (
                <span style={{ fontWeight: 400, marginLeft: 'var(--spacing-xs)' }}>
                  ({project.linked_people.length})
                </span>
              )}
            </h2>
            {project.linked_people.length === 0 ? (
              <p className="detail-field-empty">No additional people linked to this project.</p>
            ) : (
              <div className="detail-people-list">
                {project.linked_people.map((person) => (
                  <div key={person.id} className="detail-person-row">
                    <Link to={`/people/${person.id}`} className="detail-person-info" style={{ flex: 1 }}>
                      <span className="detail-person-name">{person.full_name}</span>
                      {person.role_or_title && (
                        <span className="detail-person-role">{person.role_or_title}</span>
                      )}
                    </Link>
                    <Badge variant={personStatusVariant(person.status)}>
                      {person.status}
                    </Badge>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemovePerson(person.id, person.full_name)}
                      disabled={removingPersonId === person.id}
                    >
                      {removingPersonId === person.id ? 'Removing…' : 'Remove'}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {!addPersonOpen ? (
              <div style={{ marginTop: 'var(--spacing-sm)' }}>
                <Button variant="primary" size="sm" onClick={openAddPersonPanel}>
                  Connect a person
                </Button>
              </div>
            ) : (
              <div className="edit-form" style={{ marginTop: 'var(--spacing-sm)' }}>
                <div className="company-field-wrapper">
                  <Input
                    label="Search people"
                    value={selectedPersonToAdd ? selectedPersonToAdd.full_name : personSearchQuery}
                    onChange={(e) => {
                      setPersonSearchQuery(e.target.value);
                      setSelectedPersonToAdd(null);
                    }}
                    placeholder="Type a name…"
                    autoComplete="off"
                    autoFocus
                  />
                  {personSuggestionsLoading && (
                    <div className="company-suggestions-loading">
                      <LoadingSpinner size="sm" />
                    </div>
                  )}
                  {!selectedPersonToAdd && personSuggestions.length > 0 && (
                    <ul className="company-suggestions">
                      {personSuggestions.map((p) => (
                        <li key={p.id}>
                          <button
                            type="button"
                            className="company-suggestion-item"
                            onMouseDown={() => {
                              setSelectedPersonToAdd(p);
                              setPersonSuggestions([]);
                            }}
                          >
                            {p.full_name}
                            {p.company_name && (
                              <span style={{ color: 'var(--color-text-light)', marginLeft: '0.5rem', fontSize: '0.875rem' }}>
                                — {p.company_name}
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {addPersonError && <p className="edit-save-error">{addPersonError}</p>}

                <div className="edit-actions">
                  <Button
                    variant="primary"
                    onClick={handleAddPerson}
                    disabled={addingPerson || !selectedPersonToAdd}
                  >
                    {addingPerson ? 'Connecting…' : 'Connect'}
                  </Button>
                  <Button variant="secondary" onClick={() => setAddPersonOpen(false)} disabled={addingPerson}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Financial summary */}
          <Card>
            <h2 className="section-title">Financial summary</h2>
            <div className="finance-grid">
              <div className="finance-item">
                <span className="finance-label">Billed</span>
                {project.billed_amount !== null ? (
                  <span className="finance-value">{formatCurrency(project.billed_amount)}</span>
                ) : (
                  <span className="finance-value-empty">—</span>
                )}
              </div>
              <div className="finance-item">
                <span className="finance-label">Profit</span>
                {project.profit !== null ? (
                  <span className="finance-value">{formatCurrency(project.profit)}</span>
                ) : (
                  <span className="finance-value-empty">—</span>
                )}
              </div>
              <div className="finance-item">
                <span className="finance-label">Days worked</span>
                {project.days_worked !== null ? (
                  <span className="finance-value">{project.days_worked}</span>
                ) : (
                  <span className="finance-value-empty">—</span>
                )}
              </div>
              <div className="finance-item">
                <span className="finance-label">Cost</span>
                {project.cost_summary !== null ? (
                  <span className="finance-value">{formatCurrency(project.cost_summary)}</span>
                ) : (
                  <span className="finance-value-empty">—</span>
                )}
              </div>
            </div>
          </Card>

          {/* GitHub repo */}
          <Card>
            <h2 className="section-title">GitHub repository</h2>
            <div className="repo-status-block">
              <div className="detail-field">
                <span className="detail-field-label">Status</span>
                <Badge variant={repoStatusVariant(project.github_repo_status)}>
                  {project.github_repo_status}
                </Badge>
              </div>

              {repoRequestError && (
                <div className="detail-field">
                  <span className="detail-field-label">Provisioning</span>
                  <span className="detail-field-value" style={{ color: 'var(--color-danger)' }}>
                    {repoRequestError}
                  </span>
                </div>
              )}

              {(project.github_repo_status === 'not_requested' || project.github_repo_status === 'failed') && (
                <div className="detail-field">
                  <span className="detail-field-label">Action</span>
                  <Button
                    variant="primary"
                    onClick={handleRequestRepoProvisioning}
                    disabled={repoRequesting}
                  >
                    {repoRequesting
                      ? 'Requesting…'
                      : project.github_repo_status === 'failed'
                        ? 'Retry repo provisioning'
                        : 'Request repo provisioning'}
                  </Button>
                </div>
              )}

              {project.github_repo_url && (
                <div className="detail-field">
                  <span className="detail-field-label">Repo</span>
                  <a
                    href={project.github_repo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="detail-field-link"
                  >
                    {project.github_repo_full_name ?? project.github_repo_url}
                  </a>
                </div>
              )}
              {project.github_repo_error && (
                <div className="detail-field">
                  <span className="detail-field-label">Error</span>
                  <span className="detail-field-value" style={{ color: 'var(--color-danger)' }}>
                    {project.github_repo_error}
                  </span>
                </div>
              )}
            </div>
          </Card>

          {/* GitHub Repo Connections -- powers the client portal's outcomes view.
              A project may connect to more than one repo. Granting the GitHub
              App access to a repo at all is a Settings-page action now; this
              is just a plain pick-from-a-list, no GitHub OAuth round-trip.
              See project-brain/data/chat-integrations.md. */}
          <Card>
            <h2 className="section-title">
              GitHub Repo Connections
              <Link to="/docs" style={{ fontWeight: 400, fontSize: '0.85rem', marginLeft: 'var(--spacing-sm)' }}>
                How does this work?
              </Link>
            </h2>

            {chatIntegrationsLoading ? (
              <div className="dashboard-panel-loading"><LoadingSpinner size="sm" /></div>
            ) : (
              <>
                {chatIntegrations.length === 0 ? (
                  <p className="detail-field-empty">No GitHub repos connected.</p>
                ) : (
                  <div className="detail-projects-list">
                    {chatIntegrations.map((ci) => {
                      const label = `${ci.github_owner}/${ci.github_repo}`;
                      const result = testResults[ci.id];
                      return (
                        <div key={ci.id} className="repo-status-block" style={{ marginBottom: 'var(--spacing-sm)' }}>
                          <div className="detail-field">
                            <span className="detail-field-label">Repo</span>
                            <a
                              href={`https://github.com/${label}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="detail-field-link"
                            >
                              {label}
                            </a>
                            <Badge variant={chatIntegrationStatusVariant(ci.status)}>{ci.status}</Badge>
                          </div>
                          {ci.status === 'warning_all_repos' && (
                            <p className="detail-field-empty">
                              This GitHub App installation has access to <strong>all</strong> repos, not just this one. Narrow it in{' '}
                              <a href="https://github.com/settings/installations" target="_blank" rel="noopener noreferrer">
                                GitHub's installation settings
                              </a> -- the CRM can't change this itself.
                            </p>
                          )}
                          {result && (
                            <div className="detail-field">
                              <span className="detail-field-label">Last test</span>
                              <span className="detail-field-value">
                                GitHub: {result.github_ok ? 'OK' : 'failed'}
                                {result.reason ? ` — ${result.reason}` : ''}
                              </span>
                            </div>
                          )}
                          <div className="edit-actions" style={{ marginTop: 'var(--spacing-sm)' }}>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleTestConnection(ci.id)}
                              disabled={testingId === ci.id}
                            >
                              {testingId === ci.id ? 'Testing…' : 'Test connection'}
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDisconnectRepo(ci.id, label)}
                              disabled={disconnectingId === ci.id}
                            >
                              {disconnectingId === ci.id ? 'Disconnecting…' : 'Disconnect'}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!addRepoOpen ? (
                  <div style={{ marginTop: 'var(--spacing-sm)' }}>
                    <Button variant="primary" size="sm" onClick={openAddRepoPanel}>
                      Add a repo
                    </Button>
                  </div>
                ) : (
                  <div className="edit-form" style={{ marginTop: 'var(--spacing-sm)' }}>
                    <div className="input-wrapper">
                      <label className="input-label">GitHub repo</label>
                      {installationsLoading ? (
                        <LoadingSpinner size="sm" />
                      ) : (() => {
                        const connectedFullNames = new Set(chatIntegrations.map((ci) => `${ci.github_owner}/${ci.github_repo}`));
                        const available = installations.filter((r) => !connectedFullNames.has(r.full_name));
                        return available.length === 0 ? (
                          <p className="detail-field-empty" style={{ marginBottom: 'var(--spacing-sm)' }}>
                            No more authorized repos to add. Grant access to another repo from{' '}
                            <Link to="/settings">Settings</Link>.
                          </p>
                        ) : (
                          <select
                            className="input"
                            value={selectedRepoFullName}
                            onChange={(e) => setSelectedRepoFullName(e.target.value)}
                          >
                            <option value="">Select a repo…</option>
                            {available.map((r) => (
                              <option key={r.full_name} value={r.full_name}>
                                {r.full_name}
                              </option>
                            ))}
                          </select>
                        );
                      })()}
                    </div>

                    {connectError && <p className="edit-save-error">{connectError}</p>}

                    <div className="edit-actions">
                      <Button
                        variant="primary"
                        onClick={handleAddRepo}
                        disabled={addingRepo || !selectedRepoFullName}
                      >
                        {addingRepo ? 'Adding…' : 'Add repo'}
                      </Button>
                      <Button variant="secondary" onClick={() => setAddRepoOpen(false)} disabled={addingRepo}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>

          {/* Metadata */}
          <div className="detail-meta">
            <span>Created {formatDate(project.created_at)}</span>
            <span>Updated {formatDate(project.updated_at)}</span>
          </div>
        </>
      ) : (
        /* ── Edit mode ──────────────────────────────────────────────────────── */
        <Card>
          <h2 className="section-title">Edit project</h2>
          <div className="edit-form">
            <Input
              label="Project name *"
              value={form.project_name}
              onChange={(e) => setForm({ ...form, project_name: e.target.value })}
              placeholder="Project name"
            />

            <div className="input-wrapper">
              <label className="input-label">Status</label>
              <select
                className="input"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {PROJECT_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <Input
              label="Project type"
              value={form.project_type}
              onChange={(e) => setForm({ ...form, project_type: e.target.value })}
              placeholder="e.g. Discovery, Build, Retainer"
            />

            <Input
              label="Cost model"
              value={form.cost_model}
              onChange={(e) => setForm({ ...form, cost_model: e.target.value })}
              placeholder="e.g. Fixed price, Time and materials"
            />

            <Input
              label="Estimated start date"
              type="date"
              value={form.estimated_start_date}
              onChange={(e) => setForm({ ...form, estimated_start_date: e.target.value })}
            />

            <Input
              label="Started date"
              type="date"
              value={form.started_date}
              onChange={(e) => setForm({ ...form, started_date: e.target.value })}
            />

            <Input
              label="Estimated close date"
              type="date"
              value={form.estimated_close_date}
              onChange={(e) => setForm({ ...form, estimated_close_date: e.target.value })}
            />

            <Input
              label="Closed date"
              type="date"
              value={form.closed_date}
              onChange={(e) => setForm({ ...form, closed_date: e.target.value })}
            />

            <Input
              label="Billed amount (£)"
              type="number"
              value={form.billed_amount}
              onChange={(e) => setForm({ ...form, billed_amount: e.target.value })}
              placeholder="0"
            />

            <Input
              label="Days worked"
              type="number"
              value={form.days_worked}
              onChange={(e) => setForm({ ...form, days_worked: e.target.value })}
              placeholder="0"
            />

            <div className="input-wrapper">
              <label className="input-label">Notes</label>
              <textarea
                className="input edit-notes-textarea"
                value={form.general_notes}
                onChange={(e) => setForm({ ...form, general_notes: e.target.value })}
                placeholder="General notes about this project…"
                rows={5}
              />
            </div>

            {saveError && (
              <p className="edit-save-error">{saveError}</p>
            )}

            <div className="edit-actions">
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
              <Button
                variant="secondary"
                onClick={cancelEditing}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ProjectDetail;
